import { MCPTool, ToolMetaData } from 'Types/Tasks.types'
import styles from '../AgenticAnnotationTaskUI.module.less'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { ToolItem } from './ToolItem/ToolItem'
import Flex from 'antd/lib/flex'
import { JSONObject } from 'Types/Common.types'
import { PlusOutlined } from '@ant-design/icons'
import Dropdown from 'antd/lib/dropdown'
import Button from 'antd/lib/button'
import { useCallback, useMemo } from 'react'
import { clsx } from 'shared/utils/react.utils'

type ToolsContainerPropsType = {
    toolData: ToolMetaData[]
    mcpTools: MCPTool[]
    onSelectTool: (toolName: string) => void
    onReorderTools: (items: ToolMetaData[]) => void
    onDeleteTool: (toolId: string) => void
    onChange: (toolId: string, value: JSONObject) => void
    onTestToolCall: (toolId: string) => void
    readOnly: boolean
    toolCallsLoadingIds: string[]
}

export const ToolsContainer = (props: ToolsContainerPropsType) => {
    const {
        toolData,
        mcpTools,
        onSelectTool,
        onReorderTools,
        onDeleteTool,
        onChange,
        onTestToolCall,
        readOnly,
        toolCallsLoadingIds,
    } = props

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event

            if (active.id !== over?.id) {
                onReorderTools(
                    arrayMove(
                        toolData,
                        toolData.findIndex(item => item.tool_run_info.value_id === active.id),
                        toolData.findIndex(item => item.tool_run_info.value_id === over?.id)
                    )
                )
            }
        },
        [toolData, onReorderTools]
    )

    const toolItemKeys = useMemo(() => {
        return toolData.map(tool => tool.tool_run_info.value_id)
    }, [toolData])

    if (toolData.length === 0) {
        return null
    }

    return (
        <Flex className={styles['tools-container']} vertical gap={10} align="flex-start">
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
            >
                <SortableContext items={toolItemKeys} strategy={verticalListSortingStrategy}>
                    {toolData.map(tool => (
                        <ToolItem
                            key={tool.tool_run_info.value_id}
                            id={tool.tool_run_info.value_id}
                            toolItem={tool}
                            mcpTools={mcpTools}
                            onDeleteTool={onDeleteTool}
                            onChange={onChange}
                            onTestToolCall={onTestToolCall}
                            readOnly={readOnly}
                            isLoading={toolCallsLoadingIds.includes(tool.tool_run_info.value_id)}
                        />
                    ))}
                </SortableContext>
            </DndContext>

            {!readOnly && (
                <Dropdown
                    menu={{
                        items: mcpTools.map(tool => ({
                            label: (
                                <Button
                                    onClick={() => onSelectTool(tool.name)}
                                    className={styles['add-tool-call-button-label']}
                                >
                                    {tool.name}
                                </Button>
                            ),
                            value: tool.name,
                            type: 'item',
                            key: tool.name,
                        })),
                    }}
                    trigger={['click']}
                    placement="bottomRight"
                >
                    <Button
                        className={clsx(styles['add-tool-call-button'], styles['secondary-button'])}
                    >
                        <PlusOutlined /> Add Tool Call
                    </Button>
                </Dropdown>
            )}
        </Flex>
    )
}
